import { InputDialpad } from 'Components/InputDialpad';
import TransferDirectoryListItem from 'Components/TransferDirectoryListItem';
import { PhoneNumberFormat } from 'google-libphonenumber';
import { Cancelable, debounce, isEmpty } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import * as React from 'react';
import { Message } from 'semantic-ui-react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import phoneUtil from 'Utils/phoneUtil';
import { Styled } from './index.styles';

const keypadIcon = require('../../assets/images/btn-keypad-gray.svg');

export const TransferDirectory: React.FC = observer(() => {
  const [searchString, setSearchString] = React.useState<string>('');

  const debounceSearch = React.useRef<
    ((newSearchString: string) => void) & Cancelable
  >();

  const {
    conversation: { CurrentConversation },
    search,
    phoneCall: { ActivePhoneCall },
    participant: { updateMyLastReadMessage },
    pusher,
    ui: {
      selectPersonPresenceStatus,
      selectConversationUnreadCounts,
      setConversationAndTotalUnreadCount,
      loadAllPeoplesPresence,
    },
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const conversationId = CurrentConversation?.id || '';

  React.useEffect(() => {
    void search.getDirectorySearch('TRANSFER', '', 1, false);

    return () => {
      if (pusher.isOnline) ActivePhoneCall.setTransferMode(false);
    };
  }, [ActivePhoneCall, loadAllPeoplesPresence, pusher.isOnline, search]);

  const isProperPhoneNumber = (number: string) => {
    const phoneNumber = number.replace(/[^0-9+]/g, '');
    return phoneUtil.isPossibleNumberString(phoneNumber, 'US');
  };

  const toggleDialPad = () => {
    ActivePhoneCall.toggleDialPad();
  };

  const cancelTransfer = () => {
    if (ActivePhoneCall.showDialPad) toggleDialPad();

    ActivePhoneCall.clearKeyPress();
    ActivePhoneCall.setTransferMode(false);
    ActivePhoneCall.setWarmTransfer(false);
  };

  const formatNumber = (num: string): string => {
    if (phoneUtil.isPossibleNumberString(num, 'US')) {
      const parsePhoneNumber = phoneUtil.parse(num, 'US');
      return phoneUtil.format(parsePhoneNumber, PhoneNumberFormat.NATIONAL);
    } else {
      return num;
    }
  };

  const directory = search.directoryMap.TRANSFER;
  if (!directory) return null;

  const validUnknownSearch =
    !isEmpty(directory.query) &&
    isProperPhoneNumber(directory.query) &&
    directory.data &&
    directory.data.length === 0;

  const handleLoadMoreContacts = async () => {
    if (!directory.next) return null;

    await search.getNextDirectory('TRANSFER');
  };

  const handleInputChange = (newSearchString: string) => {
    setSearchString(newSearchString);

    debounceSearch.current && debounceSearch.current.cancel();

    debounceSearch.current = debounce((newSearchString: string) => {
      void search.getDirectorySearch('TRANSFER', newSearchString, 1, false);
    }, 500);

    setSearchString(newSearchString);

    debounceSearch.current(newSearchString);
  };

  // Filter contacts to only include those with phone or extension numbers
  const contactsToDisplay =
    directory?.data?.filter(
      (contact) =>
        (contact.source.phoneNumbers &&
          contact.source.phoneNumbers.length > 0) ||
        (contact.source.extensionNumbers &&
          contact.source.extensionNumbers.length > 0)
    ) ?? [];

  return (
    <>
      <Styled.SearchDialpadWrapper>
        <Styled.ToggleDialpadWrapper>
          <Styled.Image src={keypadIcon} onClick={toggleDialPad} />
          <Styled.CancelText onClick={cancelTransfer}>Cancel</Styled.CancelText>
        </Styled.ToggleDialpadWrapper>
        <InputDialpad
          phoneCallModel={ActivePhoneCall}
          searchString={searchString}
          onChange={handleInputChange}
        />
      </Styled.SearchDialpadWrapper>
      <Styled.TransferDirectory>
        <Styled.TransferDirectoryTitle>DIRECTORY</Styled.TransferDirectoryTitle>
        <Styled.Divider />
        <Styled.TransferDirectoryWrapper id="transfer-list">
          <Styled.InfiniteScroll
            dataLength={directory?.data?.length ?? 0} //This is important field to render the next data
            next={handleLoadMoreContacts}
            hasMore
            loader={<React.Fragment />}
            // below props only if you need pull down functionality
            refreshFunction={handleLoadMoreContacts}
            pullDownToRefresh
            pullDownToRefreshThreshold={2}
            scrollableTarget="transfer-list"
          >
            <Styled.TransferDirectoryList>
              {validUnknownSearch ? (
                <TransferDirectoryListItem
                  conversationId={conversationId}
                  key={'unknown-number'}
                  unknownUser={true}
                  unknownUserNumber={directory.query}
                  phoneCall={ActivePhoneCall}
                  selectPersonPresenceStatus={selectPersonPresenceStatus}
                  selectUnreadCounts={selectConversationUnreadCounts}
                  setConversationAndTotalUnreadCount={
                    setConversationAndTotalUnreadCount
                  }
                  updateMyLastReadMessage={updateMyLastReadMessage}
                  formatNumber={formatNumber}
                />
              ) : contactsToDisplay.length > 0 ? (
                contactsToDisplay.map((item) => (
                  <TransferDirectoryListItem
                    conversationId={conversationId}
                    key={item.source.id}
                    contact={item}
                    phoneCall={ActivePhoneCall}
                    selectPersonPresenceStatus={selectPersonPresenceStatus}
                    selectUnreadCounts={selectConversationUnreadCounts}
                    setConversationAndTotalUnreadCount={
                      setConversationAndTotalUnreadCount
                    }
                    updateMyLastReadMessage={updateMyLastReadMessage}
                    formatNumber={formatNumber}
                  />
                ))
              ) : !isEmpty(directory.query) &&
                !isProperPhoneNumber(directory.query) ? (
                <div>
                  <Message
                    className="no-results-message"
                    visible
                    content={`No results found for "${directory.query}"`}
                  />
                </div>
              ) : null}
            </Styled.TransferDirectoryList>
          </Styled.InfiniteScroll>
        </Styled.TransferDirectoryWrapper>
      </Styled.TransferDirectory>
    </>
  );
});
